import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
  Chip,
  Link,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  DataTable,
  Info,
  SettingsPanel,
  TableRowActions,
  TableShowMoreMenu,
  Warning,
} from '@vedmarket/ui-kit';
import {
  COLORS,
  NOTIFICATION_ACTION,
  NOTIFICATION_SEVERITY,
  NOTIFICATION_TABLE_COLUMN,
  ROUTES,
} from 'config/constants';
import THEME_COLORS from 'styles/colors.scss';

import NotificationBodyContent from '../notification-body-content';

import './table.scss';

const ALL_COLUMNS = [
  NOTIFICATION_TABLE_COLUMN.SECTION,
  NOTIFICATION_TABLE_COLUMN.MESSAGE,
  NOTIFICATION_TABLE_COLUMN.CREATED_AT,
  NOTIFICATION_TABLE_COLUMN.SETTINGS,
];
const REQUIRED_COLUMNS = [
  NOTIFICATION_TABLE_COLUMN.MESSAGE,
  NOTIFICATION_TABLE_COLUMN.SETTINGS,
];
const PREFERENCES = [
  {id: NOTIFICATION_TABLE_COLUMN.SECTION, width: 118},
  {id: NOTIFICATION_TABLE_COLUMN.MESSAGE, width: 376},
  {id: NOTIFICATION_TABLE_COLUMN.CREATED_AT, width: 112},
  {id: NOTIFICATION_TABLE_COLUMN.SETTINGS, width: 40},
];
const AVAILABLE_ACTIONS = [ NOTIFICATION_ACTION.READ, NOTIFICATION_ACTION.UNREAD ];

const NotificationsTable = ({
  notifications,
  count,
  limit,
  busy,
  onRequest,
  onAction,
}: NotificationsTableProps) => {
  const {t} = useTranslation();

  const navigate = useNavigate();

  const [ activeColumns, setActiveColumns ] = useState(ALL_COLUMNS);
  const [ selected, setSelected ] = useState<string[]>([]);

  const hasSelectedNotifications = selected.length > 0;
  const preferences = PREFERENCES.filter((column) => activeColumns.includes(column.id));

  const labels: Record<NOTIFICATION_TABLE_COLUMN, string> = {
    [NOTIFICATION_TABLE_COLUMN.SECTION]: t('notifications__text__section'),
    [NOTIFICATION_TABLE_COLUMN.MESSAGE]: t('notifications__text__message'),
    [NOTIFICATION_TABLE_COLUMN.CREATED_AT]: t('notifications__text__date'),
    [NOTIFICATION_TABLE_COLUMN.SETTINGS]: '',
  };

  const handleChangeColumn = (id: NOTIFICATION_TABLE_COLUMN) => {
    setActiveColumns((current) => current.includes(id)
      ? current.filter((column) => column !== id)
      : [ ...current, id ]);
  };

  const handleAction = async (action: NOTIFICATION_ACTION) => {
    if (busy) return;

    const selectedNotifications = notifications.filter((item) => selected.includes(String(item.id)));
    const success = await onAction({
      action,
      notifications: selectedNotifications,
    });

    if (success) setSelected([]);
  };

  const headCells = ALL_COLUMNS.map((id) => ({
    id,
    numeric: false,
    noSort: true,
    label: id === NOTIFICATION_TABLE_COLUMN.SETTINGS ? (
      <SettingsPanel
        activeColumns={activeColumns}
        activeColumnsAll={ALL_COLUMNS}
        activeColumnsMin={REQUIRED_COLUMNS}
        excludedColumnIds={REQUIRED_COLUMNS}
        menuId='notifications-settings-panel'
        selectAllLabel={t('calc__text__packaging__showHideEverything')}
        setActiveColumns={setActiveColumns}
        handleSelect={handleChangeColumn}
        getLabel={(column) => labels[column]}
      />
    ) : (
      <Typography
        variant='body2'
        sx={{fontWeight: 500, color: COLORS.BLACK_DARK, letterSpacing: 0, whiteSpace: 'nowrap'}}
      >
        {labels[id]}
      </Typography>
    ),
  })).filter((column) => activeColumns.includes(column.id));

  const rows = notifications.map((item) => {
    const hasWarningSeverity = item.severity === NOTIFICATION_SEVERITY.WARNING;
    const hasErrorSeverity = item.severity === NOTIFICATION_SEVERITY.ERROR;
    const warning = hasWarningSeverity || hasErrorSeverity;

    const infoColor = item.is_unread ? COLORS.GREEN : THEME_COLORS.GREEN_DARK;
    const iconColor = warning ? COLORS.RED : infoColor;
    const Icon = warning ? Warning : Info;
    const detailsUrl = `/${ROUTES.CABINET}/${ROUTES.NOTIFICATIONS}/${item.id}`;
    const createdAt = new Date(item.created_at).toLocaleString('ru-RU', {timeZone: 'Europe/Moscow'});

    const menuItems = [
      {
        title: (
          <Typography variant='body1' sx={{color: COLORS.BLACK_DARK}}>
            {t('actions__text__open')}
          </Typography>
        ),
        onClick: () => navigate(detailsUrl),
      },
    ];

    const action = item.is_unread ? NOTIFICATION_ACTION.READ : NOTIFICATION_ACTION.UNREAD;
    const actionRequest: NotificationsActionRequest = {action, notifications: [ item ]};

    menuItems.push({
      title: (
        <Typography variant='body1' sx={{color: COLORS.BLACK_DARK}}>
          {t(`notifications__text__${action}`)}
        </Typography>
      ),
      onClick: () => {
        if (busy) return;

        onAction(actionRequest);
      },
    });

    return {
      id: String(item.id),
      [NOTIFICATION_TABLE_COLUMN.SECTION]: (
        <Chip
          size='small'
          label={(
            <Typography
              variant='caption'
              sx={{color: COLORS.BLACK_DARK, fontSize: 12, lineHeight: '18px', letterSpacing: 0}}
            >
              {item.section_display}
            </Typography>
          )}
        />
      ),
      [NOTIFICATION_TABLE_COLUMN.MESSAGE]: (
        <Link
          className='notifications-table__message'
          href={detailsUrl}
          color='inherit'
          underline='none'
          data-unread={item.is_unread}
          data-warning={warning}
          onClick={(event) => event.stopPropagation()}
        >
          <Icon className='notifications-table__icon' style={{fill: iconColor, color: iconColor}} />
          <div className='notifications-table__copy'>
            <Typography
              className='notifications-table__title'
              variant='body2'
              sx={{color: COLORS.BLACK_DARK, lineHeight: '21px', letterSpacing: 0}}
            >
              {item.title}
            </Typography>
            <NotificationBodyContent
              body={item.body}
              applicationId={item.application_id}
              applicationUrl={item.application_url}
              variant='body2'
              onApplicationLinkClick={(event) => event.stopPropagation()}
            />
          </div>
        </Link>
      ),
      [NOTIFICATION_TABLE_COLUMN.CREATED_AT]: (
        <Typography
          variant='body2'
          sx={{color: COLORS.BLACK_DARK, lineHeight: '21px', letterSpacing: 0}}
        >
          {createdAt} МСК
        </Typography>
      ),
      [NOTIFICATION_TABLE_COLUMN.SETTINGS]: <TableShowMoreMenu menuItems={menuItems} />,
    };
  });

  return (
    <div className='notifications-table'>
      <DataTable
        rows={rows}
        count={count}
        headCells={headCells}
        preferences={preferences}
        primaryField='id'
        defaultRowsPerPage={limit}
        rowsPerPageOptions={[ 10, 25, 50 ]}
        selectedItems={selected}
        chooseItem={setSelected}
        handleSendRequest={({page = 1, limit = 10}) => {
          setSelected([]);
          onRequest({page, limit});
        }}
        actionsAfterTable={() => hasSelectedNotifications ? (
          <TableRowActions>
            {AVAILABLE_ACTIONS.map((action) => (
              <MenuItem key={action} value={action} disabled={busy} onClick={() => handleAction(action)}>
                <Typography variant='body1'>{t(`notifications__text__${action}`)}</Typography>
              </MenuItem>
            ))}
          </TableRowActions>
        ) : null}
      />
    </div>
  );
};

export default NotificationsTable;
